<template>
  <div>
    <div class="row align-items-end">
      <div
        class="col-sm-4 team-table"
        :class="[homeTeamOrderClass, {
          'home-team': teamSelect === 'home'
        }]"
        v-if="config"
      >
        <div
          class="team-period-stats-wrapper mb-4"
        >
          <div class="d-flex align-items-center stat-wrapper">
            <span class="w-100 stat-title">{{ $t('period') }}</span>
            <span
              v-for="(gamePeriod, p) in model.gamePeriods"
              :key="'homeTeamByPeriod-' + p"
              class="stat"
            >
              {{ gamePeriod.periodType === 'REGULAR' ? gamePeriod.periodNumber : 'OT' + gamePeriod.periodNumber }}
            </span>
          </div>
          <div
            v-for="tableBody in config.live.by_period.team_table" :key="'homeTeamByPeriod-' + tableBody"
            class="d-flex align-items-center stat-wrapper"
          >
            <span class="w-100 stat-title">{{ $t(`live.${tableBody}`) }}</span>
            <span
              v-for="(period, p) in model.homeTeam.by_period_stats"
              :key="'homeTeamByPeriod-' + p"
              class="stat"
            >
              {{ period ? period[tableBody] : '-' }}
            </span>
          </div>
        </div>
      </div>
      <div
        class="col-sm-4 live-buttons"
        :class="[actionsOrderClass]"
        v-if="config"
      >
        <nav aria-label="Page navigation">
          <ul class="periods pagination pagination-lg justify-content-center">
            <li v-for="gamePeriod in model.gamePeriods" :key="gamePeriod.periodType + '_' + gamePeriod.periodNumber" class="page-item"
                :class="{active: currentPeriod && currentPeriod.uuid === gamePeriod.uuid}">
              <a class="page-link" @click="setPeriod(gamePeriod.periodType, gamePeriod.periodNumber)" :data-testid="'live-period-' + gamePeriod.periodType + '-' + gamePeriod.periodNumber">
                {{ gamePeriod.periodType === 'REGULAR' ? gamePeriod.periodNumber : 'OT-' + gamePeriod.periodNumber }}
              </a>
            </li>
            <li class="page-item" v-if="model.gamePeriods && model.gamePeriods.filter(p => p.periodType === 'REGULAR').length < model.schedule.numberOfRegularPeriods">
              <a class="page-link" data-testid="live-period-add" @click="setPeriod('REGULAR', model.gamePeriods.filter(p => p.periodType === 'REGULAR').length + 1)">+</a>
            </li>
            <li class="page-item" v-else-if="model.gamePeriods">
              <a class="page-link" data-testid="live-period-add-ot" @click="setPeriod('OVERTIME', model.gamePeriods.filter(p => p.periodType === 'OVERTIME').length + 1)">+ OT</a>
            </li>
          </ul>
        </nav>
        <h6 class="text-center" data-testid="live-period-scores">({{ scoreByPeriod }})</h6>
        <h3 class="text-center mb-3" data-testid="live-score">{{ score }}</h3>
        <h2 v-if="roundScore" class="text-center text-muted mb-3">{{ roundScore }}</h2>
        <div v-if="!config.scorekeeper_stopwatch_disabled" class="text-center mb-2">
          <h4
            class="mb-0 font-weight-bold py-2 px-3 rounded text-white d-inline-block cursor-pointer"
            role="button"
            :class="{
              'bg-success': isRunning,
              'bg-danger pulse-timer': !isRunning
            }"
            @click="toggleTime"
          >
            {{ currentTimeToShow }}
          </h4>
        </div>
      </div>
      <div
        class="col-sm-4 team-table"
        :class="[awayTeamOrderClass, {
          'away-team': teamSelect === 'away'
        }]"
        v-if="config"
      >
        <div
          class="team-period-stats-wrapper mb-4"
        >
          <div class="d-flex align-items-center stat-wrapper">
            <span class="w-100 stat-title">{{ $t('period') }}</span>
            <span
              v-for="(gamePeriod, p) in model.gamePeriods"
              :key="'awayTeamByPeriodHeader-' + p"
              class="stat"
            >
              {{ gamePeriod.periodType === 'REGULAR' ? gamePeriod.periodNumber : 'OT' + gamePeriod.periodNumber }}
            </span>
          </div>
          <div
            v-for="tableBody in config.live.by_period.team_table" :key="'awayTeamByPeriod-' + tableBody"
            class="d-flex align-items-center stat-wrapper"
          >
            <span class="w-100 stat-title">{{ $t(`live.${tableBody}`) }}</span>
            <span
              v-for="(period, p) in model.awayTeam.by_period_stats"
              :key="'awayTeamByPeriod-' + p"
              class="stat"
            >
              {{ period ? period[tableBody] : '-' }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div
        class="col-sm-4 team-table pt-0 d-flex flex-column"
        :class="[homeTeamOrderClass, {
          'home-team': teamSelect === 'home',
        }]"
        v-if="config"
      >
        <h3 class="text-center unselectable w-100 mb-1">
          <a @click="selectEntity('team', 'home', model.homeTeam.uuid)" data-testid="live-team-home"
             class="btn" style="width: 100%;"
             :style="{ background: homeTeamColor }"
             :class="{'btn-success': relatedTo === 'team' && teamOn === 'home'}">
            {{ model.homeTeam.name }}
          </a>
        </h3>
        <missing-position-warning :positions="missingPositions.home" />
        <div :class="{'align-items-center': model.homeTeam.players?.length}" class="w-100">
          <div v-if="model.homeTeam.players?.length && model.hasSubstitutions">
            <a class="btn btn-sm position-relative w-100"
               @click="openSubstitutionModal" data-testid="live-open-substitution"
               :class="{ 'has-label': true }"
               style="background-color: #6c757d; color: white; border-radius: 4px 4px 0 0;">
              <span>{{ $t('game.substitution') }}</span>
              <span class="hot-key-label">,</span>
            </a>
          </div>
          <ntm-block class="w-100" :style="model.homeTeam.players?.length && model.hasSubstitutions ? 'border-radius: 0 0 4px 4px;' : 'border-radius: 4px;'">
            <div class="table-responsive">
              <div v-if="teamSelect === 'home'" class="select-player-wrapper">
                <h3 class="text-white mt-5">{{ playerSelect }}</h3>
                <h3 class="text-white">{{ playerPreview }}</h3>
              </div>
              <div v-if="!model.homeTeam.players?.length" class="row w-100 team-actions">
                <!-- <div class="col-12"><h5 class="text-center">Team actions</h5></div> -->
                <div v-for="(button, n) in buttonTeamGroups" :key="'buttonTeamGroups-' + n" class="action-wrap" :class="[button.class || 'half']">
                  <a class="btn action-button position-relative" :disabled="log.teamOn === '' || relatedTo !== 'team'"
                     :class="{
                'disabled': log.teamOn === '' || relatedTo !== 'team',
                'active': clickedButton === n,
                'has-label': button.hotKey?.label
                }"
                     @click="selectType(n)" :data-testid="'live-team-action-' + n"
                     :style="{backgroundColor: button.bgColor ? button.bgColor: '#70b9eb'}">
                    <span>{{ $t('live.sports.' + config.sport + '.game_log_buttons.team.' + n) }}</span>
                    <span v-if="button.hotKey?.label" class="hot-key-label">{{ button.hotKey?.label }}</span>
                  </a>
                </div>
              </div>
              <table v-if="model.homeTeam && model.homeTeam.players.length > 0"
                     class="table player-live-table table-hover table-vcenter"
              >
                <thead>
                <tr>
                  <th v-if="reversedTeams || !invertedTable"
                      :class="{
                      'right-table-sticky': (!invertedTable && !reversedTeams) || (reversedTeams),
                      'left-table-sticky': invertedTable && !reversedTeams
                    }"
                      class="text-center"
                  >
                    {{ $t('live.table.jersey') }}
                  </th>
                  <th v-show="!reversedTeams && invertedTable" v-for="(column, key) in config.live.player_table"
                      :key="'reversed' + column" class="text-center">
                    {{ key }}
                  </th>
                  <th
                    class="name-col"
                    :class="{
                      'right-table-sticky': (!invertedTable && !reversedTeams) || (reversedTeams),
                      'left-table-sticky': invertedTable && !reversedTeams
                    }"
                  >
                    {{ $t('live.table.name') }}
                  </th>
                  <th v-show="(!reversedTeams && !invertedTable) || (reversedTeams)"
                      v-for="(column, key) in config.live.player_table" :key="'normal' + column"
                      class="text-center">
                    {{ key }}
                  </th>
                  <th
                    v-if="!reversedTeams && invertedTable"
                    class="text-center"
                    :class="{
                      'right-table-sticky': reversedTeams,
                      'left-table-sticky': invertedTable
                    }"
                  >
                    {{ $t('live.table.jersey') }}
                  </th>
                </tr>
                </thead>
                <tbody :class="{'disabled': disableHome}">
                <tr v-for="(player, index) in homePlayersSorted"
                    class="player-row unselectable"
                    :style="{color: playerPositionColor(player)}"
                    :class="{'bg-success': log.on === player.uuid,
                    'bg-info': log.teammate === player.uuid,
                    'bg-warning': log.against === player.uuid,
                    'bench-separator': index === homePlayingCount}"
                    :key="player.uuid" @click="selectEntity('player', 'home', player.uuid)" :data-testid="'live-player-' + player.uuid">
                  <td
                    v-if="reversedTeams || !invertedTable"
                    class="font-w600 text-center number-of-jersey"
                    :class="{
                        'bg-success': log.on === player.uuid,
                        'bg-info': log.teammate === player.uuid,
                        'bg-warning': log.against === player.uuid,
                        'right-table-sticky': (!invertedTable && !reversedTeams) || (reversedTeams),
                        'left-table-sticky': invertedTable && !reversedTeams
                      }"
                  >
                    {{
                      playerPositionLabel(player) ? playerPositionLabel(player) : player.number_of_jersey
                    }}
                  </td>
                  <td v-show="!reversedTeams && invertedTable" v-for="column in config.live.player_table"
                      :key="'reversed' + column + '-' + player.uuid"
                      class="text-muted text-center" :data-testid="'live-player-stat-' + column">
                    {{ player[column] }}
                  </td>
                  <td
                    :class="{
                        'bg-success': log.on === player.uuid,
                        'bg-info': log.teammate === player.uuid,
                        'bg-warning': log.against === player.uuid,
                        'right-table-sticky pr-2': (!invertedTable && !reversedTeams) || (reversedTeams),
                        'left-table-sticky pl-2': invertedTable && !reversedTeams
                      }" class="font-w600 name-col">{{ player.name }}
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.65em;">{{ tag.name }}</span>
                    </template>
                  </td>
                  <td v-show="(!reversedTeams && !invertedTable) || (reversedTeams)"
                      v-for="column in config.live.player_table" :key="'normal' + column + '-' + player.uuid"
                      class="text-muted text-center" :data-testid="'live-player-stat-' + column">
                    {{ player[column] }}
                  </td>
                  <td
                    v-if="!reversedTeams && invertedTable"
                    class="font-w600 text-center number-of-jersey"
                    :class="{
                        'bg-success': log.on === player.uuid,
                        'right-table-sticky': !invertedTable,
                        'left-table-sticky': invertedTable
                      }">
                    {{
                      playerPositionLabel(player) ? playerPositionLabel(player) : player.number_of_jersey
                    }}
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </ntm-block>
        </div>
      </div>
      <div
        class="col-sm-4 live-buttons mb-5 d-flex flex-column"
        :class="[actionsOrderClass]"
        v-if="config"
      >
        <div v-if="model.homeTeam.players?.length && model.awayTeam.players?.length" class="row w-100 team-actions">
          <!-- <div class="col-12"><h5 class="text-center">Team actions</h5></div> -->
          <div v-for="(button, n) in buttonTeamGroups" :key="'buttonTeamGroups-' + n" class="action-wrap" :class="[button.class || 'half']">
            <a class="btn action-button position-relative" :disabled="log.teamOn === '' || relatedTo !== 'team'"
               :class="{
                'disabled': log.teamOn === '' || relatedTo !== 'team',
                'active': clickedButton === n,
                'has-label': button.hotKey?.label
                }"
               @click="selectType(n)" :data-testid="'live-team-action-' + n"
               :style="{backgroundColor: button.bgColor ? button.bgColor: '#70b9eb'}">
              <span>{{ $t('live.sports.' + config.sport + '.game_log_buttons.team.' + n) }}</span>
              <span v-if="button.hotKey?.label" class="hot-key-label">{{ button.hotKey?.label }}</span>
            </a>
          </div>
        </div>
        <div class="">
          <div class="row w-100 players-actions">
            <!-- <div class="col-12"><h5 class="text-center">Player actions</h5></div> -->
            <div v-for="(buttonGroup, n) in buttonPlayerGroups" :key="'buttonPlayerGroup-' + n" class="col-sm-12 pb-3">
              <div class="row">
                <div v-for="(button, buttonType) in buttonGroup" :key="'buttonPlayerTypes-' + buttonType"
                     :class="[button.class || 'half']"
                     class="action-wrap">
                  <a class="btn action-button position-relative" :disabled="log.on === '' || relatedTo !== 'player'"
                     :class="{
                      'disabled': log.teamOn === '' || relatedTo !== 'player' || (type !== buttonType && type !== ''),
                      'active': clickedButton === buttonType,
                      'has-label': button.hotKey?.label
                      }"
                     @click="selectType(buttonType)" :data-testid="'live-player-action-' + buttonType"
                     :style="{backgroundColor: button.bgColor ? button.bgColor: '#70b9eb'}">
                    <span>{{
                        $t('live.sports.' + config.sport + '.game_log_buttons.player.' + buttonType)
                      }}</span>
                    <span v-if="button.hotKey?.label"
                          class="hot-key-label">{{ button.hotKey?.label }}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="col-sm-4 team-table pt-0 d-flex flex-column"
        :class="[awayTeamOrderClass, {
          'away-team': teamSelect === 'away',
        }]"
        v-if="config"
      >
        <h3 class="text-center unselectable w-100 mb-1">
          <a @click="selectEntity('team', 'away', model.awayTeam.uuid)" data-testid="live-team-away"
             class="btn"
             :style="{ background: awayTeamColor }"
             :class="{'btn-success': relatedTo === 'team' && teamOn === 'away'}"
             style="width: 100%;">{{ model.awayTeam.name }}
          </a>
        </h3>
        <missing-position-warning :positions="missingPositions.away" />
        <div :class="{'align-items-center': model.awayTeam.players?.length}" class="w-100">
          <div v-if="model.awayTeam.players?.length && model.hasSubstitutions">
            <a class="btn btn-sm position-relative w-100"
               @click="openSubstitutionModal" data-testid="live-open-substitution"
               :class="{ 'has-label': true }"
               style="background-color: #6c757d; color: white; border-radius: 4px 4px 0 0;">
              <span>{{ $t('game.substitution') }}</span>
              <span class="hot-key-label">,</span>
            </a>
          </div>
          <ntm-block class="w-100" :style="model.awayTeam.players?.length && model.hasSubstitutions ? 'border-radius: 0 0 4px 4px;' : 'border-radius: 4px;'">
            <div class="table-responsive">
              <div v-if="teamSelect === 'away'" class="select-player-wrapper">
                <h3 class="text-white mt-5">{{ playerSelect }}</h3>
                <h3 class="text-white">{{ playerPreview }}</h3>
              </div>
              <div v-if="!model.awayTeam.players?.length" class="row w-100 team-actions">
                <!-- <div class="col-12"><h5 class="text-center">Team actions</h5></div> -->
                <div v-for="(button, n) in buttonTeamGroups" :key="'buttonTeamGroups-' + n" class="action-wrap" :class="[button.class || 'half']">
                  <a class="btn action-button position-relative" :disabled="log.teamOn === '' || relatedTo !== 'team'"
                     :class="{
                'disabled': log.teamOn === '' || relatedTo !== 'team',
                'active': clickedButton === n,
                'has-label': button.hotKey?.label
                }"
                     @click="selectType(n)" :data-testid="'live-team-action-' + n"
                     :style="{backgroundColor: button.bgColor ? button.bgColor: '#70b9eb'}">
                    <span>{{ $t('live.sports.' + config.sport + '.game_log_buttons.team.' + n) }}</span>
                    <span v-if="button.hotKey?.label" class="hot-key-label">{{ button.hotKey?.label }}</span>
                  </a>
                </div>
              </div>
              <table v-if="model.awayTeam && model.awayTeam.players.length > 0"
                     class="table player-live-table table-hover table-vcenter "
                     :class="!reversedTeams ? 'right-table' : 'left-table'"
              >
                <thead>
                <tr>
                  <th
                    v-if="!reversedTeams || !invertedTable"
                    class="text-center right-table-sticky"
                  >
                    {{ $t('live.table.jersey') }}
                  </th>
                  <th v-show="reversedTeams && invertedTable" v-for="(column, key) in config.live.player_table"
                      :key="'reversed' + column" class="text-center">
                    {{ key }}
                  </th>
                  <th
                    class="name-col"
                    :class="{
                      'right-table-sticky': (!reversedTeams) || (reversedTeams && !invertedTable),
                      'left-table-sticky': invertedTable && reversedTeams
                    }"
                  >
                    {{ $t('live.table.name') }}
                  </th>
                  <th v-show="(!reversedTeams) || (reversedTeams && !invertedTable)"
                      v-for="(column, key) in config.live.player_table" :key="'normal' + column"
                      class="text-center">
                    {{ key }}
                  </th>
                  <th
                    v-if="reversedTeams && invertedTable"
                    :class="{
                      'right-table-sticky': (!invertedTable && !reversedTeams) || (reversedTeams),
                      'left-table-sticky': invertedTable && reversedTeams
                    }"
                    class="text-center"
                  >
                    {{ $t('live.table.jersey') }}
                  </th>
                </tr>
                </thead>
                <tbody :class="{'disabled': disableAway}">
                <tr v-for="(player, index) in awayPlayersSorted"
                    class="player-row unselectable"
                    :style="{color: playerPositionColor(player)}"
                    :class="{'bg-success': log.on === player.uuid,
                    'bg-info': log.teammate === player.uuid,
                    'bg-warning': log.against === player.uuid,
                    'bench-separator': index === awayPlayingCount}"
                    :key="player.uuid" @click="selectEntity('player', 'away', player.uuid)" :data-testid="'live-player-' + player.uuid">
                  <td v-if="!reversedTeams || !invertedTable"
                      class="right-table-sticky font-w600 text-center number-of-jersey"
                      :class="{
                        'bg-success': log.on === player.uuid,
                        'bg-info': log.teammate === player.uuid,
                        'bg-warning': log.against === player.uuid
                      }">{{
                      playerPositionLabel(player) ? playerPositionLabel(player) : player.number_of_jersey
                    }}
                  </td>
                  <td v-show="reversedTeams && invertedTable" v-for="column in config.live.player_table"
                      :key="'reversed' + column + player.uuid"
                      class="text-muted text-center" :data-testid="'live-player-stat-' + column">
                    {{ player[column] }}
                  </td>
                  <td
                    :class="{
                      'bg-success': log.on === player.uuid,
                      'bg-info': log.teammate === player.uuid,
                      'bg-warning': log.against === player.uuid,
                      'right-table-sticky pr-2': (!reversedTeams) || (reversedTeams && !invertedTable),
                      'left-table-sticky pl-2': invertedTable && reversedTeams
                    }" class="font-w600 name-col">{{ player.name }}
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.65em;">{{ tag.name }}</span>
                    </template>
                  </td>
                  <td v-show="(!reversedTeams) || (reversedTeams && !invertedTable)"
                      v-for="column in config.live.player_table" :key="'normal' + column + player.uuid"
                      class="text-muted text-center" :data-testid="'live-player-stat-' + column">
                    {{ player[column] }}
                  </td>
                  <td v-if="reversedTeams && invertedTable" class="font-w600 text-center number-of-jersey"
                      :class="{
                        'bg-success': log.on === player.uuid,
                        'right-table-sticky': !reversedTeams,
                        'left-table-sticky': reversedTeams
                      }">{{
                      playerPositionLabel(player) ? playerPositionLabel(player) : player.number_of_jersey
                    }}
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </ntm-block>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-12 text-center action-wrapper mt-10">
        <button class="btn btn-warning" data-testid="live-end-game" @click="endGame()">
          {{$t('game.endGame')}}
        </button>
      </div>
    </div>
    <div v-if="showSubstitutionModal" class="substitution-modal-overlay" @keydown="handleSubstitutionKeyDown" @click.self="closeSubstitutionModal" tabindex="0" ref="substitutionModal">
      <div class="substitution-modal">
        <div class="modal-header d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <h3 class="mb-0 mr-3 text-truncate" style="max-width: 250px;">{{ $t('game.substitution') }}</h3>
            <div class="d-flex align-items-center justify-content-between bg-info rounded p-1 position-relative">
              <div class="btn-group btn-group-sm mr-1 border border-white rounded">
                <button class="btn btn-sm text-white"
                        :class="substitutionMode === 'classic' ? 'btn-primary' : 'btn-outline-primary'"
                        style="border: none !important;"
                        @click="substitutionMode !== 'classic' && toggleSubstitutionMode()">
                  {{ $t('game.classic_mode') || 'Classic' }}
                </button>
                <button class="btn btn-sm text-white"
                        :class="substitutionMode === 'on-court' ? 'btn-primary' : 'btn-outline-primary'"
                        style="border: none !important;"
                        @click="substitutionMode !== 'on-court' && toggleSubstitutionMode()">
                  {{ $t('game.on_court_mode') || 'On Court' }}
                </button>
              </div>
              <div class="hot-key-label-static" title="Shortcut: M">
                M
              </div>
            </div>
          </div>

          <div class="d-flex align-items-center">
            <button type="button"
                    class="btn btn-sm btn-dual bg-info"
                    @click="toggleSidebar('substitutions')">
              <i class="fa fa-history text-white"></i>
            </button>
          </div>
        </div>
        <div class="modal-body p-0">
          <div class="team-headers-fixed border-bottom bg-light px-3 py-2">
            <div class="row">
              <div class="col-6 border-right">
                <h4 class="mb-0 d-flex justify-content-between align-items-center">
                  <span class="text-truncate mr-2">{{ substitutionLeftTeam.name }}</span>
                  <span class="badge" :class="{
                    'badge-danger': substitutionDiffLeft.next > maxTeamPlayersInGame,
                    'badge-success': substitutionDiffLeft.next === maxTeamPlayersInGame,
                    'badge-warning': substitutionDiffLeft.next < maxTeamPlayersInGame
                  }">
                    {{ substitutionDiffLeft.next }} / {{ maxTeamPlayersInGame }}
                  </span>
                </h4>
              </div>
              <div class="col-6">
                <h4 class="mb-0 d-flex justify-content-between align-items-center">
                  <span class="text-truncate mr-2">{{ substitutionRightTeam.name }}</span>
                  <span class="badge" :class="{
                    'badge-danger': substitutionDiffRight.next > maxTeamPlayersInGame,
                    'badge-success': substitutionDiffRight.next === maxTeamPlayersInGame,
                    'badge-warning': substitutionDiffRight.next < maxTeamPlayersInGame
                  }">
                    {{ substitutionDiffRight.next }} / {{ maxTeamPlayersInGame }}
                  </span>
                </h4>
              </div>
            </div>
          </div>
          <div class="modal-scroll-content p-3">
            <div v-if="substitutionMode === 'on-court'" class="row">
              <div class="col-6 border-right">
                <h5 class="text-primary border-bottom pb-1 d-flex justify-content-between align-items-center">
                  {{ $t('game.select_players_on_court') || 'Select players on court' }}
                  <button class="btn btn-xs border-0 py-0"
                          :class="isTeamAnySelected(leftTeamKey) ? 'btn-primary' : 'btn-outline-primary'"
                          style="font-size: 0.75rem;"
                          @click="toggleOnCourtSelection(leftTeamKey)">
                    {{ isTeamAnySelected(leftTeamKey) ? ($t('game.unselect_all_players') || 'Unselect All') : ($t('game.select_current_lineup') || 'Select Current Lineup') }}
                  </button>
                </h5>
                <div class="player-list">
                  <div v-for="player in leftPlayersForSubstitution" :key="player.uuid"
                       class="player-item d-flex align-items-center mb-1"
                       :class="{
                         'bg-light-primary border-primary': selectedSubstitutions.includes(player.uuid),
                         'focused-player': focusedPlayerIndex !== -1 && focusedTeam === leftTeamKey && leftPlayersForSubstitution[focusedPlayerIndex] && leftPlayersForSubstitution[focusedPlayerIndex].uuid === player.uuid
                       }"
                       @click="togglePlayerSelection(player)" :data-testid="'substitution-player-' + player.uuid">
                    <input type="checkbox" :checked="selectedSubstitutions.includes(player.uuid)" class="mr-2">
                    <template v-if="previousSubstitutions.length">
                      <span v-if="previousSubstitutions.includes(player.uuid)" class="badge badge-info mr-1" title="Was in previous substitution">P</span>
                      <span v-else class="badge border-0 mr-1 text-transparent bg-transparent" style="color: transparent;">P</span>
                    </template>
                    <span class="jersey-number mr-2">{{ player.number_of_jersey }}</span>
                    <span class="player-name">{{ player.name }}</span>
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.65em;">{{ tag.name }}</span>
                    </template>
                    <div v-if="selectedSubstitutions.includes(player.uuid) && lineupPositions.length" class="ml-auto d-flex" style="gap: 2px;">
                      <button v-for="p in lineupPositions" :key="'oc-l-' + p.id"
                              class="btn btn-xs py-0 px-1"
                              :class="getDraftPosition(leftTeamKey, player.uuid) === p.id ? 'btn-warning' : 'btn-outline-secondary'"
                              style="font-size: 0.65rem;"
                              @click.stop="toggleDraftPosition(leftTeamKey, player.uuid, p.id)">
                        {{ p.id }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-6">
                <h5 class="text-primary border-bottom pb-1 d-flex justify-content-between align-items-center">
                  {{ $t('game.select_players_on_court') || 'Select players on court' }}
                  <button class="btn btn-xs border-0 py-0"
                          :class="isTeamAnySelected(rightTeamKey) ? 'btn-primary' : 'btn-outline-primary'"
                          style="font-size: 0.75rem;"
                          @click="toggleOnCourtSelection(rightTeamKey)">
                    {{ isTeamAnySelected(rightTeamKey) ? ($t('game.unselect_all_players') || 'Unselect All') : ($t('game.select_current_lineup') || 'Select Current Lineup') }}
                  </button>
                </h5>
                <div class="player-list">
                  <div v-for="player in rightPlayersForSubstitution" :key="player.uuid"
                       class="player-item d-flex align-items-center mb-1"
                       :class="{
                         'bg-light-primary border-primary': selectedSubstitutions.includes(player.uuid),
                         'focused-player': focusedPlayerIndex !== -1 && focusedTeam === rightTeamKey && rightPlayersForSubstitution[focusedPlayerIndex] && rightPlayersForSubstitution[focusedPlayerIndex].uuid === player.uuid
                       }"
                       @click="togglePlayerSelection(player)" :data-testid="'substitution-player-' + player.uuid">
                    <input type="checkbox" :checked="selectedSubstitutions.includes(player.uuid)" class="mr-2">
                    <template v-if="previousSubstitutions.length">
                      <span v-if="previousSubstitutions.includes(player.uuid)" class="badge badge-info mr-1" title="Was in previous substitution">P</span>
                      <span v-else class="badge border-0 mr-1 text-transparent bg-transparent" style="color: transparent;">P</span>
                    </template>
                    <span class="jersey-number mr-2">{{ player.number_of_jersey }}</span>
                    <span class="player-name">{{ player.name }}</span>
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.65em;">{{ tag.name }}</span>
                    </template>
                    <div v-if="selectedSubstitutions.includes(player.uuid) && lineupPositions.length" class="ml-auto d-flex" style="gap: 2px;">
                      <button v-for="p in lineupPositions" :key="'oc-r-' + p.id"
                              class="btn btn-xs py-0 px-1"
                              :class="getDraftPosition(rightTeamKey, player.uuid) === p.id ? 'btn-warning' : 'btn-outline-secondary'"
                              style="font-size: 0.65rem;"
                              @click.stop="toggleDraftPosition(rightTeamKey, player.uuid, p.id)">
                        {{ p.id }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="row">
              <div class="col-6 border-right">
                <h5 class="text-success border-bottom pb-1 d-flex justify-content-between align-items-center">
                  {{ $t('game.in_game') }}
                </h5>
                <div class="player-list mb-1">
                  <!-- Position slots (e.g. GK) -->
                  <template v-for="pos in lineupPositions">
                    <div class="position-slot-header small text-muted font-weight-bold mb-1" :key="'pos-header-left-' + pos.id"
                         :style="{ color: pos.color }">
                      {{ pos.name }}
                    </div>
                    <div v-for="player in getInGameByPosition(substitutionLeftTeam, pos.id)"
                         :key="'left-pos-' + player.uuid"
                         class="player-item d-flex align-items-center mb-1"
                         :class="{
                           'bg-light-info': selectedSubstitutions.includes(player.uuid),
                           'bg-light-success': !selectedSubstitutions.includes(player.uuid),
                           'focused-player': focusedPlayerIndex !== -1 && focusedTeam === leftTeamKey && leftPlayersForSubstitution[focusedPlayerIndex] && leftPlayersForSubstitution[focusedPlayerIndex].uuid === player.uuid
                         }"
                         @click="togglePlayerSelection(player)" :data-testid="'substitution-player-' + player.uuid">
                      <input type="checkbox" :checked="selectedSubstitutions.includes(player.uuid)" class="mr-2">
                      <template v-if="previousSubstitutions.length">
                        <span v-if="previousSubstitutions.includes(player.uuid)" class="badge badge-info mr-1">P</span>
                        <span v-else class="badge border-0 mr-1" style="color: transparent;">P</span>
                      </template>
                      <span class="jersey-number mr-2">{{ player.number_of_jersey }}</span>
                      <span class="player-name">{{ player.name }}</span>
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.65em;">{{ tag.name }}</span>
                    </template>
                      <div v-if="!selectedSubstitutions.includes(player.uuid) && lineupPositions.length" class="ml-auto d-flex" style="gap: 2px;">
                        <button v-for="p in lineupPositions" :key="'lp-' + p.id"
                                class="btn btn-xs py-0 px-1"
                                :class="getDraftPosition(leftTeamKey, player.uuid) === p.id ? 'btn-warning' : 'btn-outline-secondary'"
                                style="font-size: 0.65rem;"
                                @click.stop="toggleDraftPosition(leftTeamKey, player.uuid, p.id)">
                          {{ p.id }}
                        </button>
                      </div>
                    </div>
                    <div v-if="!getInGameByPosition(substitutionLeftTeam, pos.id).length" :key="'left-pos-empty-' + pos.id"
                         class="player-item d-flex align-items-center mb-1 border border-dashed text-muted small"
                         style="min-height: 32px; border-style: dashed !important;">
                      <span class="ml-2">{{ $t('game.no_player_for_position', { position: pos.name }) || `No ${pos.name} selected` }}</span>
                    </div>
                  </template>

                  <!-- Default players (no specific position) -->
                  <h6 v-if="lineupPositions.length" class="text-muted small border-top pt-1 mt-1 mb-1">{{ $t('game.field_players') || 'Field Players' }}</h6>
                  <div v-for="player in getInGameDefault(substitutionLeftTeam)"
                       :key="'left-def-' + player.uuid"
                       class="player-item d-flex align-items-center mb-1"
                       :class="{
                         'bg-light-info': selectedSubstitutions.includes(player.uuid),
                         'bg-light-success': !selectedSubstitutions.includes(player.uuid),
                         'focused-player': focusedPlayerIndex !== -1 && focusedTeam === leftTeamKey && leftPlayersForSubstitution[focusedPlayerIndex] && leftPlayersForSubstitution[focusedPlayerIndex].uuid === player.uuid
                       }"
                       @click="togglePlayerSelection(player)" :data-testid="'substitution-player-' + player.uuid">
                    <input type="checkbox" :checked="selectedSubstitutions.includes(player.uuid)" class="mr-2">
                    <template v-if="previousSubstitutions.length">
                      <span v-if="previousSubstitutions.includes(player.uuid)" class="badge badge-info mr-1">P</span>
                      <span v-else class="badge border-0 mr-1" style="color: transparent;">P</span>
                    </template>
                    <span class="jersey-number mr-2">{{ player.number_of_jersey }}</span>
                    <span class="player-name">{{ player.name }}</span>
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.65em;">{{ tag.name }}</span>
                    </template>
                    <div v-if="!selectedSubstitutions.includes(player.uuid) && lineupPositions.length" class="ml-auto d-flex" style="gap: 2px;">
                      <button v-for="p in lineupPositions" :key="'lf-' + p.id"
                              class="btn btn-xs py-0 px-1"
                              :class="getDraftPosition(leftTeamKey, player.uuid) === p.id ? 'btn-warning' : 'btn-outline-secondary'"
                              style="font-size: 0.65rem;"
                              @click.stop="toggleDraftPosition(leftTeamKey, player.uuid, p.id)">
                        {{ p.id }}
                      </button>
                    </div>
                  </div>

                  <div v-if="!substitutionLeftTeam.players.filter(p => p.isInGame).length" class="text-muted small">{{ $t('game.no_players_in_game') }}</div>
                  <div v-else class="text-right mb-2">
                    <button class="btn btn-xs border-0 py-0"
                            :class="isTeamAllInGameSelected(leftTeamKey) ? 'btn-primary' : 'btn-outline-primary'"
                            style="font-size: 0.75rem;"
                            @click="selectAllInGame(leftTeamKey)">{{ $t('game.select_all_players') }}</button>
                  </div>
                </div>

                <h5 class="text-muted border-bottom pb-1">{{ substitutionLeftTeam.players.filter(p => p.isInGame).length ? $t('game.bench') : $t('game.choose_starter_players') }}</h5>
                <div class="player-list">
                  <div v-for="player in substitutionLeftTeam.players.filter(p => !p.isInGame).sort((a, b) => a.number_of_jersey - b.number_of_jersey)" :key="player.uuid"
                       class="player-item d-flex align-items-center mb-1"
                       :class="{
                         'bg-light-info': selectedSubstitutions.includes(player.uuid),
                         'focused-player': focusedPlayerIndex !== -1 && focusedTeam === leftTeamKey && leftPlayersForSubstitution[focusedPlayerIndex] && leftPlayersForSubstitution[focusedPlayerIndex].uuid === player.uuid
                       }"
                       @click="togglePlayerSelection(player)" :data-testid="'substitution-player-' + player.uuid">
                    <input type="checkbox" :checked="selectedSubstitutions.includes(player.uuid)" class="mr-2">
                    <template v-if="previousSubstitutions.length">
                      <span v-if="previousSubstitutions.includes(player.uuid)" class="badge badge-info mr-1" title="Was in previous substitution">P</span>
                      <span v-else class="badge border-0 mr-1 text-transparent bg-transparent" style="color: transparent;">P</span>
                    </template>
                    <span class="jersey-number mr-2">{{ player.number_of_jersey }}</span>
                    <span class="player-name">{{ player.name }}</span>
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.65em;">{{ tag.name }}</span>
                    </template>
                    <div v-if="selectedSubstitutions.includes(player.uuid) && lineupPositions.length" class="ml-auto d-flex" style="gap: 2px;">
                      <button v-for="p in lineupPositions" :key="'lb-' + p.id"
                              class="btn btn-xs py-0 px-1"
                              :class="getDraftPosition(leftTeamKey, player.uuid) === p.id ? 'btn-warning' : 'btn-outline-secondary'"
                              style="font-size: 0.65rem;"
                              @click.stop="toggleDraftPosition(leftTeamKey, player.uuid, p.id)">
                        {{ p.id }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-6">
                <h5 class="text-success border-bottom pb-1 d-flex justify-content-between align-items-center">
                  {{ $t('game.in_game') }}
                </h5>
                <div class="player-list mb-1">
                  <!-- Position slots (e.g. GK) -->
                  <template v-for="pos in lineupPositions">
                    <div class="position-slot-header small text-muted font-weight-bold mb-1" :key="'pos-header-right-' + pos.id"
                         :style="{ color: pos.color }">
                      {{ pos.name }}
                    </div>
                    <div v-for="player in getInGameByPosition(substitutionRightTeam, pos.id)"
                         :key="'right-pos-' + player.uuid"
                         class="player-item d-flex align-items-center mb-1"
                         :class="{
                           'bg-light-info': selectedSubstitutions.includes(player.uuid),
                           'bg-light-success': !selectedSubstitutions.includes(player.uuid),
                           'focused-player': focusedPlayerIndex !== -1 && focusedTeam === rightTeamKey && rightPlayersForSubstitution[focusedPlayerIndex] && rightPlayersForSubstitution[focusedPlayerIndex].uuid === player.uuid
                         }"
                         @click="togglePlayerSelection(player)" :data-testid="'substitution-player-' + player.uuid">
                      <input type="checkbox" :checked="selectedSubstitutions.includes(player.uuid)" class="mr-2">
                      <template v-if="previousSubstitutions.length">
                        <span v-if="previousSubstitutions.includes(player.uuid)" class="badge badge-info mr-1">P</span>
                        <span v-else class="badge border-0 mr-1" style="color: transparent;">P</span>
                      </template>
                      <span class="jersey-number mr-2">{{ player.number_of_jersey }}</span>
                      <span class="player-name">{{ player.name }}</span>
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.65em;">{{ tag.name }}</span>
                    </template>
                      <div v-if="!selectedSubstitutions.includes(player.uuid) && lineupPositions.length" class="ml-auto d-flex" style="gap: 2px;">
                        <button v-for="p in lineupPositions" :key="'rp-' + p.id"
                                class="btn btn-xs py-0 px-1"
                                :class="getDraftPosition(rightTeamKey, player.uuid) === p.id ? 'btn-warning' : 'btn-outline-secondary'"
                                style="font-size: 0.65rem;"
                                @click.stop="toggleDraftPosition(rightTeamKey, player.uuid, p.id)">
                          {{ p.id }}
                        </button>
                      </div>
                    </div>
                    <div v-if="!getInGameByPosition(substitutionRightTeam, pos.id).length" :key="'right-pos-empty-' + pos.id"
                         class="player-item d-flex align-items-center mb-1 border text-muted small"
                         style="min-height: 32px; border-style: dashed !important;">
                      <span class="ml-2">{{ $t('game.no_player_for_position', { position: pos.name }) || `No ${pos.name} selected` }}</span>
                    </div>
                  </template>

                  <!-- Default players (no specific position) -->
                  <h6 v-if="lineupPositions.length" class="text-muted small border-top pt-1 mt-1 mb-1">{{ $t('game.field_players') || 'Field Players' }}</h6>
                  <div v-for="player in getInGameDefault(substitutionRightTeam)"
                       :key="'right-def-' + player.uuid"
                       class="player-item d-flex align-items-center mb-1"
                       :class="{
                         'bg-light-info': selectedSubstitutions.includes(player.uuid),
                         'bg-light-success': !selectedSubstitutions.includes(player.uuid),
                         'focused-player': focusedPlayerIndex !== -1 && focusedTeam === rightTeamKey && rightPlayersForSubstitution[focusedPlayerIndex] && rightPlayersForSubstitution[focusedPlayerIndex].uuid === player.uuid
                       }"
                       @click="togglePlayerSelection(player)" :data-testid="'substitution-player-' + player.uuid">
                    <input type="checkbox" :checked="selectedSubstitutions.includes(player.uuid)" class="mr-2">
                    <template v-if="previousSubstitutions.length">
                      <span v-if="previousSubstitutions.includes(player.uuid)" class="badge badge-info mr-1">P</span>
                      <span v-else class="badge border-0 mr-1" style="color: transparent;">P</span>
                    </template>
                    <span class="jersey-number mr-2">{{ player.number_of_jersey }}</span>
                    <span class="player-name">{{ player.name }}</span>
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.65em;">{{ tag.name }}</span>
                    </template>
                    <div v-if="!selectedSubstitutions.includes(player.uuid) && lineupPositions.length" class="ml-auto d-flex" style="gap: 2px;">
                      <button v-for="p in lineupPositions" :key="'rf-' + p.id"
                              class="btn btn-xs py-0 px-1"
                              :class="getDraftPosition(rightTeamKey, player.uuid) === p.id ? 'btn-warning' : 'btn-outline-secondary'"
                              style="font-size: 0.65rem;"
                              @click.stop="toggleDraftPosition(rightTeamKey, player.uuid, p.id)">
                        {{ p.id }}
                      </button>
                    </div>
                  </div>

                  <div v-if="!substitutionRightTeam.players.filter(p => p.isInGame).length" class="text-muted small">{{ $t('game.no_players_in_game') }}</div>
                  <div v-else class="text-right mb-2">
                    <button class="btn btn-xs border-0 py-0"
                            :class="isTeamAllInGameSelected(rightTeamKey) ? 'btn-primary' : 'btn-outline-primary'"
                            style="font-size: 0.75rem;"
                            @click="selectAllInGame(rightTeamKey)">{{ $t('game.select_all_players') }}</button>
                  </div>
                </div>

                <h5 class="text-muted border-bottom pb-1">{{ substitutionRightTeam.players.filter(p => p.isInGame).length ? $t('game.bench') : $t('game.choose_starter_players') }}</h5>
                <div class="player-list">
                  <div v-for="player in substitutionRightTeam.players.filter(p => !p.isInGame).sort((a, b) => a.number_of_jersey - b.number_of_jersey)" :key="player.uuid"
                       class="player-item d-flex align-items-center mb-1"
                       :class="{
                         'bg-light-info': selectedSubstitutions.includes(player.uuid),
                         'focused-player': focusedPlayerIndex !== -1 && focusedTeam === rightTeamKey && rightPlayersForSubstitution[focusedPlayerIndex] && rightPlayersForSubstitution[focusedPlayerIndex].uuid === player.uuid
                       }"
                       @click="togglePlayerSelection(player)" :data-testid="'substitution-player-' + player.uuid">
                    <input type="checkbox" :checked="selectedSubstitutions.includes(player.uuid)" class="mr-2">
                    <template v-if="previousSubstitutions.length">
                      <span v-if="previousSubstitutions.includes(player.uuid)" class="badge badge-info mr-1" title="Was in previous substitution">P</span>
                      <span v-else class="badge border-0 mr-1 text-transparent bg-transparent" style="color: transparent;">P</span>
                    </template>
                    <span class="jersey-number mr-2">{{ player.number_of_jersey }}</span>
                    <span class="player-name">{{ player.name }}</span>
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.65em;">{{ tag.name }}</span>
                    </template>
                    <div v-if="selectedSubstitutions.includes(player.uuid) && lineupPositions.length" class="ml-auto d-flex" style="gap: 2px;">
                      <button v-for="p in lineupPositions" :key="'rb-' + p.id"
                              class="btn btn-xs py-0 px-1"
                              :class="getDraftPosition(rightTeamKey, player.uuid) === p.id ? 'btn-warning' : 'btn-outline-secondary'"
                              style="font-size: 0.65rem;"
                              @click.stop="toggleDraftPosition(rightTeamKey, player.uuid, p.id)">
                        {{ p.id }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="draftMissingPositions.home.length || draftMissingPositions.away.length" class="px-3 pt-2">
          <missing-position-warning :positions="draftMissingPositions[leftTeamKey]" :team="model[leftTeamKey + 'Team'].name" />
          <missing-position-warning :positions="draftMissingPositions[rightTeamKey]" :team="model[rightTeamKey + 'Team'].name" />
        </div>
        <div class="modal-footer d-flex justify-content-between align-items-center">
          <div v-if="!config.scorekeeper_stopwatch_disabled" class="d-flex align-items-center">
            <label class="mb-0 mr-2">{{ $t('game.substitution_time') || 'Time' }}:</label>
            <div class="d-flex align-items-center">
              <input
                ref="subInput0"
                v-model="substitutionTimeInputs[0]"
                :placeholder="placeholderSubstitutionTime[0]"
                type="text"
                pattern="\d*"
                inputmode="numeric"
                maxlength="1"
                class="text-center time-input-sub"
                :class="{'has-error': substitutionInputErrors[0]}"
                @input="onSubstitutionTimeInput(0, $event, $refs)"
                @keydown="onSubstitutionTimeKeydown(0, $event, $refs)"
              >
              <input
                ref="subInput1"
                v-model="substitutionTimeInputs[1]"
                :placeholder="placeholderSubstitutionTime[1]"
                type="text"
                pattern="\d*"
                inputmode="numeric"
                maxlength="1"
                class="text-center time-input-sub"
                :class="{'has-error': substitutionInputErrors[1]}"
                @input="onSubstitutionTimeInput(1, $event, $refs)"
                @keydown="onSubstitutionTimeKeydown(1, $event, $refs)"
              >
              <span class="h5 mb-0 mx-1">:</span>
              <input
                ref="subInput2"
                v-model="substitutionTimeInputs[2]"
                :placeholder="placeholderSubstitutionTime[2]"
                type="text"
                pattern="\d*"
                inputmode="numeric"
                maxlength="1"
                class="text-center time-input-sub"
                :class="{'has-error': substitutionInputErrors[2]}"
                @input="onSubstitutionTimeInput(2, $event, $refs)"
                @keydown="onSubstitutionTimeKeydown(2, $event, $refs)"
              >
              <input
                ref="subInput3"
                v-model="substitutionTimeInputs[3]"
                :placeholder="placeholderSubstitutionTime[3]"
                type="text"
                pattern="\d*"
                inputmode="numeric"
                maxlength="1"
                class="text-center time-input-sub"
                :class="{'has-error': substitutionInputErrors[3]}"
                @input="onSubstitutionTimeInput(3, $event, $refs)"
                @keydown="onSubstitutionTimeKeydown(3, $event, $refs)"
              >
            </div>
          </div>

          <div class="d-flex align-items-center">
            <div v-if="substitutionLoading" class="mr-3">
              <i class="fa fa-spinner fa-spin mr-1"></i> {{ $t('loading') }}
            </div>
            <button class="btn btn-secondary mr-2" :disabled="substitutionLoading" @click="closeSubstitutionModal">{{ $t('cancel') }}</button>
            <button class="btn btn-primary" data-testid="substitution-confirm" :disabled="!isSubstitutionValid || substitutionLoading" @click="confirmSubstitution">{{ $t('confirm') }}</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showOptionsModal" class="options-modal-overlay" @click.self="closeOptionsModal">
      <div class="options-modal">
        <div class="options-modal-header d-flex align-items-center justify-content-between p-3">
          <button v-if="optionsModalStack.length > 1" class="btn btn-sm btn-outline-secondary mr-2" @click="optionsModalBack">
            <i class="fa fa-arrow-left"></i>
          </button>
          <span class="font-w600">{{ $t('selectOption') || 'Select option' }}</span>
          <button class="btn btn-sm btn-outline-secondary" @click="closeOptionsModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="options-modal-body p-3">
          <div class="row">
            <div
              v-for="(option, optionKey, idx) in optionsModalStack[optionsModalStack.length - 1].options"
              :key="optionKey"
              class="col-6 mb-3"
            >
              <a
                class="btn action-button w-100 text-white position-relative"
                :style="{ backgroundColor: option.bgColor || '#70b9eb' }"
                @click="selectOption(optionKey, option)" :data-testid="'live-option-' + optionKey"
              >
                <span class="hot-key-label">{{ idx + 1 }}</span>
                {{ option.label || optionKey }}
                <i v-if="option.options" class="fa fa-chevron-right ml-1"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { useLive } from '@/views/live/use/useLive'
import { useStopwatch } from '@/use/useStopwatch'
import MissingPositionWarning from '@/components/le/MissingPositionWarning'

export default {
  components: { MissingPositionWarning },
  setup () {
    const live = useLive()
    const { isRunning, isActiveCurrentStopwatch, currentTimeToShow, toggleTime } = useStopwatch()
    return { ...live, isRunning, isActiveCurrentStopwatch, currentTimeToShow, toggleTime }
  },
  data () {
    return {
      homeTeamColor: localStorage.getItem('homeTeamColor') ?? '#70b9eb',
      awayTeamColor: localStorage.getItem('awayTeamColor') ?? '#70b9eb',
      contextMenuTimeout: null
    }
  },
  computed: {
    substitutionLeftTeam () {
      return this.reversedTeams ? this.model.awayTeam : this.model.homeTeam
    },
    substitutionRightTeam () {
      return this.reversedTeams ? this.model.homeTeam : this.model.awayTeam
    },
    leftPlayersForSubstitution () {
      return this.reversedTeams ? this.awayPlayersForSubstitution : this.homePlayersForSubstitution
    },
    rightPlayersForSubstitution () {
      return this.reversedTeams ? this.homePlayersForSubstitution : this.awayPlayersForSubstitution
    },
    substitutionDiffLeft () {
      return this.reversedTeams ? this.substitutionDiff.away : this.substitutionDiff.home
    },
    substitutionDiffRight () {
      return this.reversedTeams ? this.substitutionDiff.home : this.substitutionDiff.away
    },
    leftTeamKey () {
      return this.reversedTeams ? 'away' : 'home'
    },
    rightTeamKey () {
      return this.reversedTeams ? 'home' : 'away'
    },
    leftPositionAssignments () {
      return this.positionAssignments[this.leftTeamKey + 'Team'] ?? {}
    },
    rightPositionAssignments () {
      return this.positionAssignments[this.rightTeamKey + 'Team'] ?? {}
    }
  },
  methods: {
    playerTagStyle (tag) {
      if (!tag || !tag.color) return { backgroundColor: '#6c757d', color: '#fff' }
      return { backgroundColor: tag.color, color: '#fff' }
    },
    rerender () {
      this.$forceUpdate()
    },
    getInGameByPosition (team, positionId) {
      return (team.players ?? [])
        .filter(p => p.isInGame && p.in_game_position === positionId)
        .sort((a, b) => a.number_of_jersey - b.number_of_jersey)
    },
    getInGameDefault (team) {
      const positionIds = (this.lineupPositions ?? []).map(p => p.id)
      return (team.players ?? [])
        .filter(p => p.isInGame && !positionIds.includes(p.in_game_position))
        .sort((a, b) => a.number_of_jersey - b.number_of_jersey)
    },
    getPositionById (positionId) {
      return (this.lineupPositions ?? []).find(p => p.id === positionId)
    }
  }
}

</script>

<style lang="scss" scoped>
.action-button:active,
.action-button.active {
  transition: none;
  box-shadow: none;
  transform: scale(0.90);
}

.player-row {
  color: #575757;
}

.half {
  max-width: 50%;
  flex: 0 0 50%;
  padding: 4px;
}

.full {
  max-width: 100%;
  flex: 0 0 100%;
  padding: 4px;
}

.btn.action-button {
  display: flex;
  justify-content: space-evenly;
  transition: 0.1s !important;
}

.bench-separator {
  border-top: 3px solid #ccc !important;
}

.bg-light-success {
  background-color: #d4edda !important;
}

.bg-light-info {
  background-color: #d1ecf1 !important;
}

.player-item.focused-player {
  background-color: #e0e0e0 !important;
  box-shadow: inset 0 0 0 2px #3498db;
}
.substitution-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000000;
}

.substitution-modal {
  background: white;
  width: 80%;
  max-height: 90%;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 5px 15px rgba(0,0,0,0.5);
}

.modal-header, .modal-footer {
  padding: 15px 20px;
  background: #f8f9fa;
}

.modal-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Prevent body itself from scrolling */
}

.team-headers-fixed {
  flex-shrink: 0;
}

.modal-scroll-content {
  flex-grow: 1;
  overflow-y: auto;
}

.player-list {
  margin-bottom: 20px;
}

.player-item {
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background 0.2s;
}

.player-item:hover {
  background: #f0f0f0;
}

.jersey-number {
  font-weight: bold;
  width: 20px;
  display: inline-block;
}

.home-team .table-responsive, .away-team .table-responsive {
  position: relative;
}

.home-team .table-responsive .select-player-wrapper, .away-team .table-responsive .select-player-wrapper {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  background: #0f74a8;
  text-align: center;
  color: white;
  font-size: 1rem;
}

.periods {
  margin-bottom: 20px;
}

.team-table {
  padding-top: 20px;
}

.player-live-table tbody tr {
  cursor: pointer;
}

.player-live-table.table td.number-of-jersey {
  padding: 0.5rem;
  font-size: 16px;
}

.player-live-table tbody tr.selected_player {
  background: #f1f1f1;
}

.player-live-table {
  font-size: 0.7rem;
}

.player-live-table.table th, .player-live-table.table td {
  padding: 0.2rem;
}

.player-live-table.table thead th {
  letter-spacing: 0;
  font-size: 0.7rem;
}

.left-table-sticky {
  position: sticky;
  right: 0;
  background-color: white;
  z-index: 10;
}

.left-table-sticky.name-col {
  right: 25px;
}

.right-table-sticky {
  position: sticky;
  left: 0;
  background-color: white;
  z-index: 10;
}

.right-table-sticky.name-col {
  left: 25px;
}

.player-live-table tbody tr:hover .right-table-sticky,
.player-live-table tbody tr:hover .left-table-sticky {
  background-color: #f1f1f1;
}

.player-live-table tbody td {
  white-space: nowrap;
}

.disabled .right-table-sticky,
.disabled .left-table-sticky {
  background-color: #dfdfdf;
}

.live-buttons a {
  width: 100%;
  color: white !important;
  font-size: 0.9rem
}

.pulse-timer {
  animation: pulse-timer 1.2s infinite;
}

@keyframes pulse-timer {
  0% {
    transform: scale(0.95);
  }

  70% {
    transform: scale(1);
  }

  100% {
    transform: scale(0.95);
  }
}

.live-buttons .periods a {
  color: black !important;
}

.cursor-pointer {
  cursor: pointer;
}

tbody.disabled {
  background: #dfdfdf;
}

.side_action_wrapper h3 {
  padding-top: 10px;
  padding-bottom: 10px;
}

.side_action_wrapper h3 {
  min-height: 50px;
}

.accept-log {
  box-shadow: 0 0 10px 2px rgba(0, 0, 0, 0.3);

  &.btn:disabled, &.btn.disabled {
    opacity: 1;
  }
}

.accept-log.disabled {
  background-color: #00b0e8;
}

.team-period-stats-wrapper {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  font: {
    size: 14px;
    weight: 600;
  }
  line-height: 1;

  .stat {
    background-color: #fff;
    height: 32px;
    width: 32px;
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;

    &-wrapper {
      gap: 4px;
    }

    &-title {
      background-color: #fff;
      height: 32px;
      display: flex;
      align-items: center;
      padding: 0 8px;
      border-radius: 4px;
    }
  }
}

.time-input-sub {
  outline: none;
  margin: 3px;
  padding: 5px;
  width: 35px;
  height: 35px;
  text-align: center;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 3px;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.2);
  caret-color: transparent;
}

.time-input-sub:focus {
  box-shadow: 0 0 6px rgba(0, 123, 255, 0.8);
  border-color: #007bff;
}

.time-input-sub.has-error {
  box-shadow: 0 0 6px rgba(220, 53, 69, 0.8);
  border: 1px solid rgba(220, 53, 69, 0.5);
}

.arrow-btn-sub {
  color: #888;
  font-size: 0.7rem;
  line-height: 1;
  transition: color 0.15s;
}

.arrow-btn-sub:hover {
  color: #007bff;
  text-decoration: none;
}

.arrow-btn-sub:focus {
  outline: none;
  box-shadow: none;
}

.time-input-sub::-webkit-outer-spin-button,
.time-input-sub::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.time-input-sub[type=number] {
  -moz-appearance: textfield;
}

.options-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000001;
}

.options-modal {
  background: #fff;
  border-radius: 8px;
  width: 340px;
  max-width: 95vw;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.options-modal-header {
  border-bottom: 1px solid #e4e7ed;
}

.options-modal-body {
  min-height: 100px;
}
</style>
